import type { LobbyGame } from '../api/getLobby';
import type { BingoContestSummary, BingoMyCard } from '../api/getMyGames';

/**
 * One Game Grid (Bingo) contest the user holds a card in, assembled for My Games.
 *
 * Bingo exposes no "my games" endpoint, so this is stitched from two sources: the tenant
 * contest list (`/api/bingo`) and the per-contest card (`/card` — the thing that decides
 * membership at all, since a card IS the entry). The lobby listing is consulted last, for the
 * presentation fields the Bingo API does not carry (entry count, free-text prize copy) — a
 * contest can be entered by direct link and be absent from the lobby entirely, which is why
 * nothing here depends on finding a lobby row. Same shape as `lobbyMyGamesSurvivor`, for the
 * same reason.
 */
export interface BingoMyGame {
  pickemId: string;
  title: string;
  sport: string | null;
  startUtc: string | null;
  /** `pickems.pickem_status` — free text, compared case-insensitively throughout. */
  pickemStatus: string | null;
  card: BingoMyCard;
  /**
   * Lobby free-text prize copy when present. Prefer this over formatting `prizePool` so a
   * non-cash headline ("Win a signed jersey") still wins the column — same order Pick'em uses.
   */
  prizeText: string | null;
  /** Advertised pot from the Bingo list. Used when lobby prize copy is absent. */
  prizePool: number | null;
  entryCount: number | null;
  /**
   * The contest's own link, straight from the backend.
   *
   * Null when the backend could not build one (unknown tenant or game type). The card falls back
   * to deriving a URL from the lobby listing, which only works while a contest of this game type
   * is actually listed — the failure this field exists to remove.
   */
  publicUrl: string | null;
}

/**
 * Pattern tiers in the order the contest ranks them, worst to best.
 *
 * Mirrors `BINGO_PATTERN_RANK` in the backend's `BingoPatternSets`. Held as an ordered list
 * rather than a rank map because the only question asked of it here is "which of these achieved
 * tiers is the best one", and an index answers that directly.
 */
const BINGO_TIERS_ASCENDING = [
  'line',
  'two_lines',
  'three_lines',
  'four_corners',
  'x',
  'full_card',
] as const;

/**
 * Tier display names, matching the Game Grid app's own win banners
 * (`BINGO_TIER_WIN_LABEL` in `apps/bingo/src/types/bingo.ts`) so a player reads the same words
 * on their card and in the lobby.
 */
const BINGO_TIER_LABEL: Record<string, string> = {
  line: '1 Line',
  two_lines: '2 Lines',
  three_lines: '3 Lines',
  four_corners: '4 Corners',
  x: 'X',
  full_card: 'Full Card',
};

export function bingoTierLabel(kind: string | null | undefined): string | null {
  if (!kind) return null;
  return BINGO_TIER_LABEL[kind.trim().toLowerCase()] ?? null;
}

export function buildBingoMyGames(input: {
  contests: BingoContestSummary[];
  cardById: Map<string, BingoMyCard>;
  lobbyGames: LobbyGame[];
}): BingoMyGame[] {
  const { contests, cardById, lobbyGames } = input;
  const games: BingoMyGame[] = [];

  for (const contest of contests) {
    const card = cardById.get(contest.id);
    // No card — not the user's game. This is the ONLY membership test: the contest list is
    // tenant-wide and includes contests they have never opened.
    if (!card) continue;

    const lobbyGame = lobbyGames.find(g => g.pickem_id === contest.id);

    games.push({
      pickemId: contest.id,
      title: (contest.titleEn || 'Game Grid').trim(),
      sport: contest.sport ?? null,
      startUtc: contest.startUtc ?? null,
      pickemStatus: contest.pickemStatus ?? null,
      card,
      prizeText: lobbyGame?.prize_text_en ?? lobbyGame?.prize_text_fr ?? null,
      prizePool:
        typeof contest.prizePool === 'number' && Number.isFinite(contest.prizePool)
          ? contest.prizePool
          : null,
      entryCount: lobbyGame?.entry_count ?? null,
      publicUrl: contest.publicUrl ?? null,
    });
  }

  // Soonest first. Contests with no start time sort last rather than to 1970, so an unscheduled
  // contest does not lead the list.
  return games.sort((a, b) => {
    const ta = toTime(a.startUtc) || Number.MAX_SAFE_INTEGER;
    const tb = toTime(b.startUtc) || Number.MAX_SAFE_INTEGER;
    if (ta !== tb) return ta - tb;
    return a.pickemId.localeCompare(b.pickemId);
  });
}

function toTime(iso: string | null | undefined): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * The best tier this card has actually won.
 *
 * Read from `payable`, not `achieved`: the two differ by containment — an X contains the two
 * diagonals that make it, so a card with an X reports three achieved tiers and one payable one.
 * The payable set is what the contest pays on, so it is what a summary row should headline.
 */
export function bestBingoTier(game: BingoMyGame): string | null {
  const payable = game.card.outcome?.payable ?? [];
  if (payable.length === 0) return null;

  let best: string | null = null;
  let bestRank = -1;
  for (const kind of payable) {
    const rank = BINGO_TIERS_ASCENDING.indexOf(kind.trim().toLowerCase() as never);
    if (rank > bestRank) {
      bestRank = rank;
      best = kind.trim().toLowerCase();
    }
  }
  return best;
}

/** How many of the card's own tiles have hit, and how many there are to hit. */
export function bingoTileProgress(game: BingoMyGame): { hit: number; total: number } {
  const cells = game.card.cells ?? [];
  // The free centre is not a tile the player can win or lose, so counting it would flatter
  // every card by one and make a blank 5x5 read as 1/25.
  const playable = cells.filter(cell => !cell.isFreeSpace);
  const hit = playable.filter(cell => cell.state === 'hit').length;
  return { hit, total: playable.length };
}

/**
 * Has the CONTEST finished? Not "has the player stopped winning" — a card that completed its
 * line in the first quarter still belongs in Live until the contest itself is over, the same
 * rule Survivor's Live/Recent split uses.
 */
export function isBingoContestComplete(game: BingoMyGame): boolean {
  const status = String(game.pickemStatus ?? '').toUpperCase();
  if (status === 'COMPLETE' || status === 'COMPLETED' || status === 'FINISHED') return true;

  // A status write can lag, so a card whose every playable tile has settled is finished too.
  // Guarded on there being tiles at all: an empty board would otherwise satisfy "all settled"
  // vacuously and drop a live contest into Recent.
  const playable = (game.card.cells ?? []).filter(cell => !cell.isFreeSpace);
  return playable.length > 0 && playable.every(cell => Boolean(cell.settledAt));
}

/**
 * Has the contest begun?
 *
 * Two signals, matching the other game types: the status the feed writes, plus the start time
 * as a backstop, because a status write can lag the whistle and a contest already on screen
 * must not still be filed under Upcoming.
 *
 * A settled tile is a third, and the strongest: tiles only settle once their fixture is under
 * way, so it is proof the contest is live whatever the status column says.
 */
export function isBingoGameLive(game: BingoMyGame, now: number = Date.now()): boolean {
  const status = String(game.pickemStatus ?? '').toUpperCase();
  if (status === 'INPLAY' || status === 'LIVE' || status === '1') return true;
  if ((game.card.cells ?? []).some(cell => !cell.isFreeSpace && cell.settledAt)) return true;
  const start = toTime(game.startUtc);
  return start > 0 && start <= now;
}

/**
 * The contest's week, when its title states one.
 *
 * Bingo contests carry no week of their own — unlike Survivor, which has a `survivor_weeks` row
 * per week — and nothing in the platform maps a date to an NFL week number, so there is nothing
 * to derive one from. Operators do name these contests for their slate ("NFL Week 3 Game Grid"),
 * so the title is the only place the week actually exists.
 *
 * Returns null when the title does not state one, and the card omits the week rather than
 * guessing: a wrong week is worse than no week on a contest a player is deciding whether to open.
 */
export function bingoWeekLabel(title: string | null | undefined): string | null {
  if (!title) return null;
  const match = title.match(/\bweeks?\s*#?\s*(\d{1,2})\b/i);
  if (!match) return null;
  const week = Number(match[1]);
  if (!Number.isInteger(week) || week < 1 || week > 30) return null;
  return `Week ${week}`;
}

/**
 * What the My Games Prize Pool column shows.
 *
 * Lobby free-text first (can name a non-cash prize), then the numeric pot from the Bingo list —
 * finished contests leave the lobby, so without that fallback the column was a permanent dash.
 */
export function bingoPrizePoolDisplay(game: BingoMyGame): string {
  const text = game.prizeText?.trim();
  if (text) return text;
  if (game.prizePool != null && game.prizePool > 0) {
    return `$${game.prizePool.toLocaleString()}`;
  }
  return '—';
}
